<template>
    <div class="selector-wrapper" :class="{ mobile: getMobileAppState}">
        <dropdown-cards
        :params="{ headerType: 'description-header' }"
        :options="options"
        :selectedOption="selectedOption"
        @clicked="emit('clicked', $event)"/>
        <!-- Balances -->
        <div v-dragscroll="getMobileAppState" class="text-blocks-container">
            <text-block
            :params="getAvailableBalanceParams"
            :icon="getAvailableBalanceIcon"
            :colorIconPreset="getAvailableBalanceIconColor"
            :applyEllipsis="!getMobileAppState"/>

            <text-block
            :params="getAccountingBalanceParams"
            :icon="getAccountingBalanceIcon"
            :colorIconPreset="getAccountingBalanceIconColor"
            :applyEllipsis="!getMobileAppState"/>
        </div>
        <!-- Gradient for overflow @mobile -->
        <div v-if="getMobileAppState" class="mobile-gradient"/>
    </div>
</template>

<script setup>
import DropdownCards from '@/components/generic/dropdowns/cards/Card'
import TextBlock from '@/components/generic/textBlocks/Generic'
/* Vue */
import { computed } from 'vue'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
/* Aux */
import { amountFormat } from '@/assets/scripts/amounts'
/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
/* mapGetters */
const { getMobileAppState } = mapGetters()
/* Emmitters */
const emit = defineEmits(['clicked'])

/** props received
@selectedOption { String, Number }
@options { Array } each item is an Object with:
	- id { String, Number }
	- productName { String }
	- specialStatusClass { String }  possible classes: blocked or alert, default is no specialStatusClass
	- cardNumber { String }
	- cardDescription { String }
	- cardBalance { Number }
	- cardCurrency { String }
*/
const props = defineProps({
	selectedOption: [String, Number],
	options: Array
})

const selectedCard = computed(() => {
	return props.selectedOption ? props.options.find(option => option.id === props.selectedOption) : ''
})

const getAvailableBalanceParams = computed(() => {
    const availableBalance = selectedCard.value.balance
    return {
        label: t('selectorWithDetails.card.availableBalance'),
        content: availableBalance || availableBalance === 0 ? amountFormat(Math.abs(availableBalance), selectedCard.value.cardCurrency) : '-',
        classes: 'amount-icon'
    }
})
const getAvailableBalanceIcon = computed(() => {
    const availableBalance = selectedCard.value.balance
    if (!availableBalance || availableBalance === 0) return
    return availableBalance > 0 ? 'plus' : 'minus'
})
const getAvailableBalanceIconColor = computed(() => {
    const availableBalance = selectedCard.value.balance
    if (!availableBalance || availableBalance === 0) return
    return availableBalance > 0 ? 'positive' : 'negative'
})

const getAccountingBalanceParams = computed(() => {
    const accountingBalance = selectedCard.value.accountingBalance
    return {
        label: t('selectorWithDetails.card.cardBalance'),
        content: accountingBalance || accountingBalance === 0 ? amountFormat(Math.abs(accountingBalance), selectedCard.value.cardCurrency) : '-',
        classes: 'amount-icon'
    }
})

const getAccountingBalanceIcon = computed(() => {
    const accountingBalance = selectedCard.value.accountingBalance
    if (!accountingBalance || accountingBalance === 0) return
    return accountingBalance > 0 ? 'plus' : 'minus'
})

const getAccountingBalanceIconColor = computed(() => {
    const accountingBalance = selectedCard.value.accountingBalance
    if (!accountingBalance || accountingBalance === 0) return
    return accountingBalance > 0 ? 'positive' : 'negative'
})
</script>

<style lang="scss" scoped>
.selector-wrapper {
    height: 64px;
    width: 558px;
    padding: 0;

    display: flex;
    flex-direction: row;
    gap: 16px;

    z-index: 5;
    border-radius: 16px;
    box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.08);
    background-color: var(--secondary-light-4);

    .dropdown-wrapper {
        width: 226px;

        :deep(.dropdown-content) .header-wrapper .header {
            border-radius: 16px;
        }

        :deep(.dropdown-animation-wrapper) {
            width: 312px;
            left: -20px;
        }
    }

    .text-blocks-container {
        padding-right: 16px;

        display: flex;
        gap: 16px;
        align-items: center;
        flex-grow: 1;

        overflow: hidden;
        border-left: 1px solid var(--secondary-variant-5);

        .text-blocks-wrapper {
            width: 50%;

            :deep(label) {
                @include H8-Regular;
            }

            :deep(.content-wrapper) {
                margin-top: 0;

                svg {
                    width: 12px;
                    height: 12px;
                }

                .content {
                    @include H6-Medium;
                }
            }

        }
    }

    // MOBILE STYLES
    &.mobile {
        height: 128px;
        padding: 0;
        gap: 0;

        position: relative;
        flex-direction: column;

        .dropdown-wrapper,
        .text-blocks-container {
            width: 100%;
            height: 64px;
            align-items: center;
            padding: 0 16px;
        }

        .dropdown-wrapper {
            justify-content: center;
            border-bottom: 1px solid var(--secondary-variant-5);
        }

        .text-blocks-container {
            padding-right: 0;
            border-left: none;

            .text-blocks-wrapper {
                width: auto;
                height: auto;

                :deep(.content) {
                    white-space: nowrap;
                }

                &:last-of-type {
                    padding-right: 30px;
                }
            }
        }

        .mobile-gradient {
            height: 64px;
            width: 44px;

            position: absolute;
            bottom: 0;
            right: 0;

            border-radius: 16px;
            background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, var(--white) 100%)
        }
    }
}
</style>
